import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { KeySquare } from 'lucide-react';
import {
  getSecretByNameAsync,
  selectSelectedSecret,
  selectSecretsLoading,
  selectSecretsError,
  clearSelectedSecret
} from '../redux/getSecrets/reducer';
import { InteractionRequiredAuthError } from '@azure/msal-common';
import { AppDispatch } from '../redux/store';
import '../styles/css/secret.css';
import {useTranslation} from "react-i18next";
import brand from "../brand";

interface SecretDetailProps {
  secretName: string;
  onBack: () => void;
}

const SecretDetail: React.FC<SecretDetailProps> = ({ secretName, onBack }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const secret = useSelector(selectSelectedSecret);
  const loading = useSelector(selectSecretsLoading);
  const error = useSelector(selectSecretsError);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);

  useEffect(() => {
    const fetchSecretDetails = async () => {
      try {
        await dispatch(getSecretByNameAsync(secretName)).unwrap();
      } catch (err: any) {
        console.error('Error fetching secret details:', err);
        if (err instanceof InteractionRequiredAuthError ||
            (err?.name === 'InteractionRequiredAuthError') ||
            (typeof err === 'string' && err.includes('Authentication'))) {
          setSessionExpired(true);
        }
      }
    };

    fetchSecretDetails();

    return () => {
      dispatch(clearSelectedSecret());
    };
  }, [dispatch, secretName]);

  const formatDate = (dateString: string): string => {
    if (!dateString) return 'N/A';
    if (!dateString.includes('/')) {
      return dateString;
    }
    const parts = dateString.split('/');
    return `${parts[0]}-${parts[1]}-${parts[2]}`;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <span className="loading-text">{t('Loading secret details...')}</span>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div className="error-container">
        <h3>Session Expired</h3>
        <p>Your session has expired or your token is invalid. Please refresh the page to continue.</p>
        <button onClick={() => window.location.reload()} className="refresh-button">
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <div className="secret-detail">
      <div className="secret-detail-header">
        <h2 style={{fontFamily: brand.titleFont}} className="secondaryColor">
          <KeySquare className="title-icon secondaryColor" />
          {t('Secret Details')}
        </h2>
        <button onClick={onBack} className="back-button request-update-btn-text-color" style={{fontFamily: brand.bodyFont}}>
          {t('Back to List')}
        </button>
      </div>

      <div className="secret-detail-content" style={{fontFamily: brand.bodyFont}}>
        <div className="detail-row">
          <dt className="detail-label">{t('Secret Name')}</dt>
          <dd className="detail-value">{secret?.secret_name || 'N/A'}</dd>
        </div>

        <div className="detail-row">
          <dt className="detail-label">{t('Tenant Name')}</dt>
          <dd className="detail-value">{secret?.tenant_name || 'N/A'}</dd>
        </div>

        <div className="detail-row">
          <dt className="detail-label">{t('Tenant ID')}</dt>
          <dd className="detail-value">{secret?.tenant_ID || 'N/A'}</dd>
        </div>

        <div className="detail-row">
          <dt className="detail-label">{t('Expiry Date')}</dt>
          <dd className="detail-value">
            {formatDate(secret?.expiry_date)}
            {(secret?.expiry_date.includes('Never') || secret?.daysUntilExpiry !== undefined || secret?.daysExpired !== undefined) && (
              <span className={`status-badge ${secret.isExpired ? 'invalid' : 'valid'}`}>
                {secret.isExpired ? 'Invalid' : 'Valid'}
              </span>
            )}
          </dd>
        </div>

        <div className="detail-row">
          <dt className="detail-label">{t('Days Until Expiry')}</dt>
          <dd className="detail-value">{secret?.daysUntilExpiry || 'N/A'}</dd>
        </div>

        <div className="detail-row">
          <dt className="detail-label">{t('ARN')}</dt>
          <dd className="detail-value">{secret?.arn || 'N/A'}</dd>
        </div>
      </div>
    </div>
  );
};

export default SecretDetail;